import { Link } from "wouter";
import tlcLogo from "@assets/TLC_Logo_1776354004749.webp";
import { allServiceLinks, serviceAreaLinks } from "@/lib/site-navigation";

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#1877F2" aria-hidden="true">
      <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987H7.898V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
    </svg>
  );
}

function InstagramIcon({ className }: { className?: string }) {
  const gradientId = "instagram-gradient-footer";
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
      <defs>
        <radialGradient id={gradientId} cx="30%" cy="107%" r="150%">
          <stop offset="0%" stopColor="#fdf497" />
          <stop offset="5%" stopColor="#fdf497" />
          <stop offset="45%" stopColor="#fd5949" />
          <stop offset="60%" stopColor="#d6249f" />
          <stop offset="90%" stopColor="#285AEB" />
        </radialGradient>
      </defs>
      <path
        fill={`url(#${gradientId})`}
        d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"
      />
    </svg>
  );
}

function HouzzIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#4DBC15" aria-hidden="true">
      <path d="M1.27 0v24h8.05v-7.56h5.36V24h8.05V10.37L7.83 6.07V0H1.27z" />
    </svg>
  );
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M23.52 12.27c0-.85-.08-1.67-.22-2.45H12v4.64h6.47c-.28 1.5-1.13 2.77-2.4 3.62v3.01h3.89c2.28-2.1 3.56-5.2 3.56-8.82z" />
      <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.95-2.91l-3.89-3.01c-1.08.72-2.46 1.15-4.06 1.15-3.13 0-5.78-2.11-6.73-4.95H1.26v3.11C3.24 21.3 7.29 24 12 24z" />
      <path fill="#FBBC05" d="M5.27 14.28A7.2 7.2 0 014.9 12c0-.79.14-1.56.37-2.28V6.61H1.26A11.97 11.97 0 000 12c0 1.94.46 3.77 1.26 5.39l4.01-3.11z" />
      <path fill="#EA4335" d="M12 4.77c1.76 0 3.34.61 4.58 1.79l3.44-3.44C17.95 1.19 15.24 0 12 0 7.29 0 3.24 2.7 1.26 6.61l4.01 3.11C6.22 6.88 8.87 4.77 12 4.77z" />
    </svg>
  );
}

export function Footer() {
  return (
    <footer className="bg-[#1a1a1a] text-white pt-16 pb-8">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          <div>
            <Link href="/">
              <img
                src={tlcLogo}
                alt="TLC Associates LLC - Licensed & Insured"
                width={200}
                height={125}
                className="w-40 h-auto mb-6 cursor-pointer"
              />
            </Link>
            <p className="text-gray-400 mb-6 max-w-sm">
              TLC Associates LLC provides landscape maintenance and property services throughout Norfolk County, Massachusetts.
            </p>
            <address className="not-italic space-y-2 text-gray-300">
              <p>
                149 West Central Street<br />
                Franklin, MA 02038
              </p>
              <p className="font-medium text-white mt-4 pt-2">
                <a href="tel:5085051539" aria-label="Call TLC Associates LLC at 508-505-1539" className="hover:text-[#c84060] transition-colors">
                  508-505-1539
                </a>
              </p>
              <p>Licensed & Insured</p>
            </address>
            <div className="flex items-center space-x-4 mt-6">
              <a
                href="https://www.facebook.com/profile.php?id=100067314530324"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TLC Associates on Facebook"
                className="opacity-80 hover:opacity-100 transition-opacity"
              >
                <FacebookIcon className="w-6 h-6" />
              </a>
              <a
                href="https://www.instagram.com/tlc.property/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TLC Associates on Instagram"
                className="opacity-80 hover:opacity-100 transition-opacity"
              >
                <InstagramIcon className="w-6 h-6" />
              </a>
              <a
                href="https://www.houzz.com/pro/webuser-650824446/tlc-associates-llc"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TLC Associates on Houzz"
                className="opacity-80 hover:opacity-100 transition-opacity"
              >
                <HouzzIcon className="w-6 h-6" />
              </a>
              <a
                href="https://www.google.com/maps/place/TLC+Associates+LLC/@42.0839203,-71.4038514,521m/data=!3m2!1e3!4b1!4m6!3m5!1s0x41e4c38a9c0c6c4d:0x677dc05e3422c4b7!8m2!3d42.0839203!4d-71.4038514!16s%2Fg%2F11svq_jj4h?entry=ttu&g_ep=EgoyMDI2MDcwNi4wIKXMDSoASAFQAw%3D%3D"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TLC Associates on Google"
                className="opacity-80 hover:opacity-100 transition-opacity"
              >
                <GoogleIcon className="w-6 h-6" />
              </a>
            </div>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-6">Our Services</h3>
            <ul className="space-y-3">
              {allServiceLinks.map((service) => (
                <li key={service.path}>
                  <Link
                    href={service.path}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  href="/gallery"
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Project Gallery
                </Link>
              </li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-6">Service Areas</h3>
            <ul className="space-y-3">
              {serviceAreaLinks.map((area) => (
                <li key={area.path}>
                  <Link href={area.path} className="text-gray-400 hover:text-white transition-colors">
                    {area.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-6">Start Your Project</h3>
            <p className="text-gray-400 mb-6">
              Ready to transform your property? Get in touch for a detailed walkthrough and free estimate.
            </p>
            <Link
              href="/contact#estimate-form"
              className="inline-block bg-[#c84060] hover:bg-[#b53553] text-white px-6 py-3 rounded-lg font-medium tracking-wide transition-all"
            >
              Request Your Free Estimate
            </Link>
          </div>
        </div>
        
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
          <p>© {new Date().getFullYear()} TLC Associates LLC. All rights reserved.</p>
          <p className="mt-4 md:mt-0">
            Website by{' '}
            <a href="https://morecommercialjobs.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-white transition-colors">
              More Commercial Jobs
            </a>
          </p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <Link href="/privacy" className="hover:text-white transition-colors">
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
